import React from "react";
import { change } from "redux-form";
import { dataFieldsOptions } from "@schmodel/core";

import ApplicationLayout, { applicationStyles as styles } from "../ApplicationLayout";
import WithUserForm from "../../Register/WithUserForm";
import { Cta, SubmitError } from "../fields";

export const BRAND_NAME_MAX = 35;
export const BRAND_DESCRIPTION_MIN = 33;
export const BRAND_DESCRIPTION_MAX = 88;

const TALENT_GROUPS = [
  { name: "On camera", items: dataFieldsOptions.skills.filter((s) => s.onCamera) },
  { name: "Off camera", items: dataFieldsOptions.skills.filter((s) => !s.onCamera) },
];

const BRAND_GROUPS = [
  {
    name: "Fashion",
    items: dataFieldsOptions.brandTypes.filter(
      (b) => b.group === "Fashion apparel" || b.group === "Fashion accessories"
    ),
  },
  { name: "Beauty", items: dataFieldsOptions.brandTypes.filter((b) => b.group === "Beauty products") },
];

export const isBrandValid = (brandName, brandDescription) => {
  const name = (brandName || "").trim();
  const description = (brandDescription || "").trim();
  return (
    name.length > 0 &&
    description.length >= BRAND_DESCRIPTION_MIN &&
    description.length <= BRAND_DESCRIPTION_MAX
  );
};

const descriptionError = (value) => {
  const description = (value || "").trim();
  if (!description) return "Please describe your brand in a sentence or two.";
  if (description.length < BRAND_DESCRIPTION_MIN) {
    return `Please add a little more, at least ${BRAND_DESCRIPTION_MIN} characters.`;
  }
  return "";
};

const Tick = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="#000" strokeWidth="2" aria-hidden="true">
    <path d="M2.5 7.5 5.5 10.5 11.5 3.5" />
  </svg>
);

const TagGroup = ({ name, items, selected, onToggle }) => (
  <div className={styles.group}>
    <div className={styles.fieldLabel}>{name}</div>
    <div className={styles.tagRow}>
      {items.map((item) => {
        const on = selected.includes(item.value);
        return (
          <button
            key={item.value}
            type="button"
            aria-pressed={on}
            className={`${styles.tag} ${on ? styles.tagSelected : ""}`}
            onClick={() => onToggle(item.value)}
          >
            {on && <Tick />}
            {item.label}
          </button>
        );
      })}
    </div>
  </div>
);

export default class StepSkills extends React.Component {
  state = {
    activeTab: "talent",
    skills: [],
    brandTypes: [],
    brandName: "",
    brandDescription: "",
    tried: false,
  };

  initialized = false;

  initFromUser = (currentUser) => {
    // Fill the selections from the userdata once. this.initialized prevents this from
    // running again on subsequent renders (every skill toggle calls setState -> re-render),
    // which would overwrite the user's in-progress selections.
    if (this.initialized || !currentUser) return;
    this.initialized = true;
    const hasBrandTypes = currentUser.brandTypes?.length > 0;
    const hasSkills = currentUser.skills?.length > 0;
    this.setState({
      activeTab: hasBrandTypes ? "brand" : "talent",
      skills: hasSkills ? currentUser.skills : [],
      brandTypes: hasBrandTypes ? currentUser.brandTypes : [],
      brandName: currentUser.brandName || "",
      brandDescription: currentUser.brandDescription || "",
    });
  };

  setActiveTab = (activeTab) => this.setState({ activeTab, tried: false });

  selectionKey = () => (this.state.activeTab === "talent" ? "skills" : "brandTypes");

  selection = () => this.state[this.selectionKey()];

  handleToggleItem = (value) =>
    this.setState((state) => {
      const key = this.selectionKey();
      const current = state[key];
      return {
        [key]: current.includes(value)
          ? current.filter((one) => one !== value)
          : [...current, value],
      };
    });

  async onSubmit(values, _, props) {
    const { activeTab, brandName, brandDescription } = this.state;
    const isTalent = activeTab === "talent";
    const checkedItems = this.selection();

    if (checkedItems.length === 0 || (!isTalent && !isBrandValid(brandName, brandDescription))) {
      this.setState({ tried: true });
      return;
    }

    if (isTalent) {
      props.dispatch(change("signup", "skills", checkedItems));
      const formattedValues = WithUserForm.formatUserSkillsInput({ ...values, skills: checkedItems });
      await props.updateUserSkills({ variables: { input: formattedValues } });
    } else {
      props.dispatch(change("signup", "brandTypes", checkedItems));
      const formattedValues = WithUserForm.formatUserBrandInput({
        ...values,
        brandTypes: checkedItems,
        brandName: brandName.trim(),
        brandDescription: brandDescription.trim(),
      });
      await props.updateUserBrand({ variables: { input: formattedValues } });
    }
    return props.machine.service.send({ type: "NEXT" });
  }

  renderBrandFields() {
    const { brandName, brandDescription, tried } = this.state;
    const nameError = tried && !brandName.trim() ? "Please enter your brand's name." : "";
    const descError = tried ? descriptionError(brandDescription) : "";
    const count = brandDescription.length;

    return (
      <>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="brandName">Brand name</label>
          <div className={`${styles.box} ${styles.inputBox} ${nameError ? styles.boxInvalid : ""}`}>
            <input
              id="brandName"
              className={styles.input}
              type="text"
              autoComplete="organization"
              maxLength={BRAND_NAME_MAX}
              value={brandName}
              // Letters, digits and spaces only
              onChange={(e) => this.setState({ brandName: e.target.value.replace(/[^a-zA-Z0-9 ]/g, "") })}
            />
          </div>
          {nameError && <div className={styles.error}>{nameError}</div>}
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="brandDescription">About your brand</label>
          <div className={`${styles.box} ${descError ? styles.boxInvalid : ""}`}>
            <textarea
              id="brandDescription"
              className={`${styles.input} ${styles.textarea}`}
              rows={4}
              maxLength={BRAND_DESCRIPTION_MAX}
              placeholder="e.g. Sustainable tailoring for women, made in Portugal"
              value={brandDescription}
              onChange={(e) => this.setState({ brandDescription: e.target.value })}
            />
          </div>
          <div className={`${styles.counter} ${count >= BRAND_DESCRIPTION_MAX - 8 ? styles.counterNearLimit : ""}`}>
            {count} / {BRAND_DESCRIPTION_MAX}
          </div>
          {descError && <div className={styles.error}>{descError}</div>}
        </div>
      </>
    );
  }

  render() {
    const { activeTab, tried, brandName, brandDescription } = this.state;
    const isTalent = activeTab === "talent";
    const checkedItems = this.selection();
    const groups = isTalent ? TALENT_GROUPS : BRAND_GROUPS;
    const nothingPicked = tried && checkedItems.length === 0;
    const invalid = checkedItems.length === 0 || (!isTalent && !isBrandValid(brandName, brandDescription));

    return (
      <WithUserForm onSubmit={(values, _, props) => this.onSubmit(values, _, props)}>
        {({ form: { handleSubmit, submitting }, machine, userQuery: { data } }) => {
          this.initFromUser(data?.currentUser);

          const footer = (
            <>
              {tried && invalid && <SubmitError>Some fields need your attention.</SubmitError>}
              {checkedItems.length > 0 && (
                <div className={styles.footerStatus}>
                  {checkedItems.length === 1 ? "1 selected" : `${checkedItems.length} selected`}
                </div>
              )}
              <div className={styles.actions}>
                <Cta submitting={submitting}>Continue</Cta>
                <p className={styles.footerNote}>
                  Having trouble? <a href="https://wa.me/443330166335">Contact us</a>
                </p>
              </div>
            </>
          );

          return (
            <ApplicationLayout
              as="form"
              onSubmit={handleSubmit}
              step={4}
              onBack={() => machine.service.send("BACK")}
              title="What do you do?"
              footer={footer}
            >
              <div
                role="radiogroup"
                aria-label="I am applying as"
                className={styles.segmented}
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={isTalent}
                  className={`${styles.segment} ${isTalent ? styles.segmentSelected : ""}`}
                  onClick={() => this.setActiveTab("talent")}
                >
                  Creative talent
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={!isTalent}
                  className={`${styles.segment} ${!isTalent ? styles.segmentSelected : ""}`}
                  onClick={() => this.setActiveTab("brand")}
                >
                  Brand
                </button>
              </div>

              {!isTalent && this.renderBrandFields()}

              <div>
                <p className={styles.intro} style={{ margin: 0 }}>
                  {isTalent ? "Choose everything that applies." : "What do you make? Choose everything that applies."}
                </p>
                {groups.map((group) => (
                  <TagGroup
                    key={group.name}
                    name={group.name}
                    items={group.items}
                    selected={checkedItems}
                    onToggle={this.handleToggleItem}
                  />
                ))}
                {nothingPicked && (
                  <div className={styles.error} style={{ marginTop: 16 }}>
                    {isTalent ? "Choose at least one role." : "Choose at least one product type."}
                  </div>
                )}
              </div>
            </ApplicationLayout>
          );
        }}
      </WithUserForm>
    );
  }
}
